import { queryClient } from '@/data';
import { SBK_BETSLIP_LOG_TAG, SbkBetSlipState, initialState } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { getEvent, getEventKeys } from '@/feature/event-details-sbk/hooks/use-event';
import { AcceptMultiplierChanges, defaultUserSettings } from '@/hooks/use-auth-user-settings';
import { logger } from '@/utils/logging';
import { runStoreUpdate } from '@/utils/zustand';

import {
    BetSlipEvent,
    BetSlipMarket,
    BetSlipOption,
    BetType,
    SbkBetSlip,
    SbkPromo,
    Selection,
    StakeInputError,
} from '../types';
import { isAutoApplyPromo } from './promo-criteria/auto-apply-promos';
import { retainReferrer } from './share-referrer';

/**
 * Remove all selections for betslip
 */
export const clearBetSlip = (state: Pick<SbkBetSlip, 'product' | 'userSettings' | 'producerStatuses'>) => ({
    ...initialState,
    product: state.product,
    userSettings: state.userSettings,
    producerStatuses: state.producerStatuses,
});

/** clearBetSlip keeps userSettings for the signed-in user; logging out must not leak them to the next one. */
export const resetBetSlipOnLogout = (state: Pick<SbkBetSlip, 'product' | 'userSettings' | 'producerStatuses'>) => ({
    ...clearBetSlip(state),
    userSettings: null,
});

/**
 * Resets betslip to initial state, but keeps the bet selections
 */
export const keepBetSlipSelections = (state: SbkBetSlipState) => ({
    ...initialState,
    product: state.product,
    eventOrder: state.eventOrder,
    selectionOrder: state.selectionOrder,
    options: state.options,
    markets: state.markets,
    events: state.events,
    selections: state.selections,
    sgpEventDisabled: state.sgpEventDisabled,
    sgpOdds: state.sgpOdds,
    sgpOddsChanges: state.sgpOddsChanges,
    oddsChanges: state.oddsChanges,
    producerStatuses: state.producerStatuses,
    userSettings: state.userSettings,
    referrer: state.referrer,
});

/**
 * Resets betslip to initial state, but keeps selections that were NOT wagered on as singles.
 * Used after successful singles-only submission to retain picks the user didn't stake.
 */
export const keepUnstakedSelections = (state: SbkBetSlipState) => {
    const stakedSelectionIds = new Set(state.submittedState.singlesBets.map(bet => bet.id));

    // Collect remaining selections and their referenced IDs in a single pass
    const remainingSelections: Record<string, Selection> = {};
    const remainingSelectionIds = new Set<string>();
    const remainingEventIds = new Set<string>();
    const remainingMarketIds = new Set<string>();
    const remainingOptionIds = new Set<string>();

    for (const [id, selection] of Object.entries(state.selections)) {
        if (!stakedSelectionIds.has(id)) {
            remainingSelections[id] = selection;
            remainingSelectionIds.add(id);
            remainingEventIds.add(selection.eventId);
            remainingMarketIds.add(selection.marketId);
            remainingOptionIds.add(selection.optionId);
        }
    }

    if (remainingSelectionIds.size === 0) {
        return clearBetSlip(state);
    }

    // Filter ordering arrays and related data to only remaining references
    const selectionOrder = state.selectionOrder.filter(id => remainingSelectionIds.has(id));
    const eventOrder = state.eventOrder.filter(id => remainingEventIds.has(id));

    const events: Record<string, BetSlipEvent> = {};
    for (const eventId of remainingEventIds) {
        if (state.events[eventId]) {
            events[eventId] = state.events[eventId];
        }
    }

    const markets: Record<string, BetSlipMarket> = {};
    for (const marketId of remainingMarketIds) {
        if (state.markets[marketId]) {
            markets[marketId] = state.markets[marketId];
        }
    }

    const options: Record<string, BetSlipOption> = {};
    for (const optionId of remainingOptionIds) {
        if (state.options[optionId]) {
            options[optionId] = state.options[optionId];
        }
    }

    const stakedIds = [...stakedSelectionIds];
    const notStaked = ([key]: [string, unknown]) => !stakedIds.some(id => key.includes(id));

    // filter out sgp odds, sgp odds changes, and odds changes that include staked selections
    const sgpOdds = Object.fromEntries(Object.entries(state.sgpOdds).filter(notStaked));
    const sgpOddsChanges = Object.fromEntries(Object.entries(state.sgpOddsChanges).filter(notStaked));
    const oddsChanges = Object.fromEntries(Object.entries(state.oddsChanges).filter(notStaked));

    return {
        ...initialState,
        product: state.product,
        sgpEventDisabled: state.sgpEventDisabled,
        closedSelections: state.closedSelections,
        producerStatuses: state.producerStatuses,
        userSettings: state.userSettings,
        selections: remainingSelections,
        eventOrder,
        selectionOrder,
        options,
        markets,
        events,
        sgpOdds,
        sgpOddsChanges,
        oddsChanges,
        referrer: retainReferrer(state.referrer, remainingSelections),
    };
};

/**
 * Toggle selection status. The status is the current state of the selection, either true (on) or false (off).
 */
export const toggleComboSelectionStatus = (state: SbkBetSlip, selectionId: string) => {
    const newState = { ...state };
    const hasSgpError = state.sgpEventDisabled[state.selections[selectionId].eventId];
    const isTogglingOn = !state.selections[selectionId].isComboEnabled;
    if (hasSgpError && isTogglingOn) {
        return forceToggleOnlySelection(newState, selectionId);
    } else {
        return {
            ...state,
            selections: {
                ...state.selections,
                [selectionId]: {
                    ...state.selections[selectionId],
                    isComboEnabled: !state.selections[selectionId].isComboEnabled,
                },
            },
            sgpTemporaryIssue: {
                ...state.sgpTemporaryIssue,
                [state.selections[selectionId].eventId]: false,
            },
        };
    }
};

const forceToggleOnlySelection = (state: SbkBetSlip, selectionId: string) => {
    const eventId = state.selections[selectionId].eventId;
    const eventSelections = Object.values(state.selections).filter(s => s.eventId === eventId);
    const newSelections = eventSelections.reduce((acc, s) => {
        return {
            ...acc,
            [s.id]: {
                ...s,
                isComboEnabled: s.id === selectionId,
            },
        };
    }, {});
    return {
        ...state,
        selections: {
            ...state.selections,
            ...newSelections,
        },
    };
};

/**
 * Update Stake
 */
export const updateStake = (
    state: SbkBetSlip,
    betId: string,
    stake: number,
    displayStake: string,
    betType: BetType
) => {
    return {
        ...state,
        bets: {
            ...state.bets,
            [betId]: {
                ...state.bets[betId],
                id: betId,
                betType,
                stake,
                displayStake,
            },
        },
    };
};

/**
 * Update Stake Input Errors
 */
export const updateStakeInputErrors = (state: SbkBetSlip, betId: string, stakeInputError?: StakeInputError) => {
    if (!state.bets[betId]) {
        return state;
    }
    return {
        ...state,
        bets: {
            ...state.bets,
            [betId]: {
                ...state.bets[betId],
                stakeInputError,
            },
        },
    };
};

/**
 * Clear bet stake - resets stake to 0 and displayStake to empty
 */
export const clearBetStake = (state: SbkBetSlip, betId: string) => {
    if (!state.bets[betId]) {
        return state;
    }
    return {
        ...state,
        bets: {
            ...state.bets,
            [betId]: {
                ...state.bets[betId],
                stake: 0,
                displayStake: '',
                stakeInputError: undefined,
            },
        },
    };
};

type PayloadProps = {
    event_id: string;
    bets: {
        side_bet_id: string;
        side_bet_option_id: string;
    }[];
};

export const sgpOddsKeys = {
    all: ['sgpOdds'] as const,
    sgpOdds: (payload: PayloadProps) => [...sgpOddsKeys.all, payload] as const,
};

/**
 * For the two following cases:
 *  1. User has accepted all odds changes enabled
 *  2. User has higher odds changes enabled and the current odds change match the condition
 * The function will return the value to simulate show/hide animation for the odds change indicator for 2 seconds.
 * Otherwise it will return null (no animation show).
 */
export const getOddsChangeIndicatorTimeout = ({
    state,
    oddsChanges,
}: {
    state: SbkBetSlip;
    oddsChanges: { [x: string]: number };
}) => {
    let timeout = null;
    if (Object.keys(oddsChanges).length > 0) {
        if (state.oddsChangeTimeout) {
            clearTimeout(state.oddsChangeTimeout);
        }
        const { userSettings = defaultUserSettings } = state;
        timeout = setTimeout(() => {
            if (userSettings?.accept_multiplier_changes === AcceptMultiplierChanges.ALL) {
                runStoreUpdate(() => state.actions.clearOddsChanges());
            }
            if (userSettings?.accept_multiplier_changes === AcceptMultiplierChanges.HIGHER) {
                runStoreUpdate(() => state.actions.clearHigherOddsChanges());
            }
        }, 2000);
    }
    return timeout;
};

/**
 * Set selected promo for a specific bet
 */
export const setSelectedPromo = (
    state: SbkBetSlipState,
    betId: string,
    promo: SbkPromo | null
): Partial<SbkBetSlipState> => ({
    selectedPromos: { ...state.selectedPromos, [betId]: promo },
});

/**
 * User-initiated removal. Removing an auto-apply promo also stops auto-apply for the rest of the slip.
 */
export const removeSelectedPromo = (state: SbkBetSlipState, betId: string): Partial<SbkBetSlipState> => {
    const promo = state.selectedPromos[betId];
    return {
        ...setSelectedPromo(state, betId, null),
        isAutoPromoDismissed: state.isAutoPromoDismissed || (!!promo && isAutoApplyPromo(promo)),
    };
};

export const autoApplyPromos = (
    state: SbkBetSlipState,
    promosByBetId: Record<string, SbkPromo>
): Partial<SbkBetSlipState> => ({
    selectedPromos: { ...state.selectedPromos, ...promosByBetId },
});

/**
 * Enable betr bucks for a bet and remove its applied promo - the two are mutually exclusive
 */
export const enableBetrBucksAndRemovePromo = (state: SbkBetSlipState, betId: string): Partial<SbkBetSlipState> => ({
    ...setSelectedPromo(state, betId, null),
    isBetrBucksEnabled: { ...state.isBetrBucksEnabled, [betId]: true },
});

/**
 * Fetches an event using react-query
 * @param eventId - The event id
 */
export const getEventDetails = async (eventId: string) => {
    try {
        return await queryClient.fetchQuery({
            queryKey: getEventKeys().byId(eventId),
            queryFn: () => getEvent(eventId),
        });
    } catch (e) {
        logger.warn(SBK_BETSLIP_LOG_TAG, `Failed to fetch event ${eventId}`, e);
    }
};
